import React, { useMemo, useRef } from 'react';
import T from 'prop-types';
import cx from 'classnames';

import ChartLegend from '../../ChartLegend';
import Skeleton from '../../../../../components/Skeleton';
import { SmartChart } from '../../../../../components/Chart';
import useChartLegend, {
  useHoveredItemClassName,
  useToggleSeriesVisibility,
} from '../../../hooks/useChartLegend';
import { getChartConfig } from './config';
import {
  aiCohortsColorsMap,
  aiCohortsFillColorsMap,
  aiCohortsMap,
} from '../../../configs/constants';
import { advancedPartialFilters } from '../../../configs/chart-config';

const emptyObject = {};
const cohortOrder = Object.keys(aiCohortsMap);

const UsageTrendChart = ({ isPending, series = emptyObject, isPress }) => {
  const chartRef = useRef(null);

  const { activeItem, hoveredItem, onClick, onMouseOver, onMouseOut } = useChartLegend();
  const hoveredItemClassName = useHoveredItemClassName(series, hoveredItem, cohortOrder);
  useToggleSeriesVisibility(chartRef, activeItem);

  const metaSeriesData = useMemo(
    () =>
      cohortOrder.reduce(
        (acc, cohort) => ({
          ...acc,
          ...(series[cohort]
            ? {
                [cohort]: {
                  type: 'areaspline',
                  color: aiCohortsColorsMap[cohort],
                  fillColor: aiCohortsFillColorsMap[cohort],
                  usePassedFillColor: true,
                  name: aiCohortsMap[cohort],
                  id: cohort,
                  // manage visible via useToggleSeriesVisibility to preserve built-in animation
                },
              }
            : {}),
        }),
        {}
      ),
    [series]
  );

  const chartConfig = useMemo(
    () => getChartConfig({ onClick, onMouseOver, onMouseOut, isPress }),
    [onClick, onMouseOver, onMouseOut, isPress]
  );

  return isPending ? (
    <Skeleton height={272} className="d-flex" />
  ) : (
    <div className={cx('chart-container', hoveredItemClassName)}>
      <SmartChart
        ref={chartRef}
        chartId="app-impact-chart"
        series={series}
        metaSeriesData={metaSeriesData}
        config={chartConfig}
        allowPartialPeriods
        filters={advancedPartialFilters}
      />
      <ChartLegend
        series={Object.values(metaSeriesData)}
        activeItem={activeItem?.id || hoveredItem?.id}
        showTooltip
        onClick={onClick}
        onMouseOver={onMouseOver}
        onMouseOut={onMouseOut}
      />
    </div>
  );
};

UsageTrendChart.propTypes = {
  isPending: T.bool.isRequired,
  series: T.object,
  isPress: T.bool,
};

export default UsageTrendChart;
